import CheckCircle from "@mui/icons-material/CheckCircle";
import { Alert, Box, Button, Container, Paper, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import type { EmailChangeConfirmResponse } from "../api/auth";
import { confirmEmailChangeNew, confirmEmailChangeOld } from "../api/auth";
import { PageLoadingState } from "../components/pages/PageLoadingState";

type ConfirmKind = "confirm-old" | "confirm-new";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function CheckWithCircle({
  confirmed,
  size = 48,
}: {
  confirmed: boolean;
  size?: number;
}) {
  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {/* Spinning ring only */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: "2px dashed",
          borderColor: confirmed ? "success.main" : "divider",
          animation: confirmed ? "none" : `${spin} 8s linear infinite`,
        }}
      />
      <CheckCircle
        sx={{
          position: "relative",
          fontSize: size * 0.6,
          color: confirmed ? "success.main" : "text.disabled",
        }}
      />
    </Box>
  );
}

export function EmailChangeConfirm({ kind }: { kind: ConfirmKind }) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<EmailChangeConfirmResponse["status"] | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid link");
      return;
    }
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const confirm =
      kind === "confirm-old"
        ? () => confirmEmailChangeOld(token)
        : () => confirmEmailChangeNew(token);
    confirm()
      .then((res) => {
        setStatus(res.status);
        if (res.status === "applied") {
          timeoutId = setTimeout(
            () => navigate("/settings?email_change=success", { replace: true }),
            1500,
          );
        }
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Confirmation failed");
      });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [kind, token, navigate]);

  const newConfirmed = status === "confirmed_new" || status === "applied";
  const oldConfirmed = status === "confirmed_old" || status === "applied";
  const bothConfirmed = status === "applied";

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Confirm email change
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {kind === "confirm-old"
              ? "Confirm from your current email."
              : "Confirm from your new email."}
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          {error ? (
            <Stack spacing={2}>
              <Alert severity="error">{error}</Alert>
              <Button component={RouterLink} to="/settings" variant="contained">
                Back to settings
              </Button>
            </Stack>
          ) : status == null ? (
            <PageLoadingState variant="inline" message="Confirming email change…" />
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0,
                }}
              >
                <CheckWithCircle confirmed={newConfirmed} />
                <Box
                  sx={{
                    width: 80,
                    height: 0,
                    borderTop: "2px",
                    borderTopStyle: bothConfirmed ? "solid" : "dotted",
                    borderTopColor: bothConfirmed ? "success.main" : "divider",
                  }}
                />
                <CheckWithCircle confirmed={oldConfirmed} />
              </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                New email · Current email
              </Typography>
              {bothConfirmed ? (
                <Alert severity="success">
                  Email updated. Redirecting to settings…
                </Alert>
              ) : (
                <Alert severity="info">
                  Click the link in your other inbox to complete the change.
                </Alert>
              )}
              {!bothConfirmed && (
                <Button component={RouterLink} to="/settings" variant="outlined" size="medium">
                  Back to settings
                </Button>
              )}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}
